import { computed, unref, type Ref, type ComputedRef } from 'vue';
import { useQuery, useQueryClient, useMutation, type UseQueryOptions } from '@tanstack/vue-query';
import type { IServiceOptions, IServiceParams } from '@/types/apiTypes';

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

/** Deeply unwraps refs/computeds into plain values. */
export function deepUnref<T>(obj: MaybeRef<T>, seen = new WeakSet()): T {
  const unwrapped = unref(obj) as T;

  if (unwrapped === null || typeof unwrapped !== 'object') {
    return unwrapped;
  }

  if (seen.has(unwrapped as unknown as object)) {
    return unwrapped;
  }
  seen.add(unwrapped as unknown as object);

  if (Array.isArray(unwrapped)) {
    return (unwrapped as unknown[]).map(item =>
      deepUnref(item as MaybeRef<unknown>, seen)
    ) as unknown as T;
  }

  const result: Record<string, unknown> = {};
  for (const key in unwrapped as Record<string, unknown>) {
    if (Object.prototype.hasOwnProperty.call(unwrapped, key)) {
      result[key] = deepUnref((unwrapped as never)[key], seen);
    }
  }
  return result as T;
}

/** Wrapper around useQuery that supports reactive params/options/enabled. */
export function useSmartQuery<TData, TError = Error>(
  baseKey: MaybeRef<readonly unknown[]>,
  fetcher: (args: {
    params: IServiceParams;
    options?: IServiceOptions;
    signal?: AbortSignal;
  }) => Promise<TData>,
  args?: {
    params?: MaybeRef<IServiceParams>;
    options?: MaybeRef<IServiceOptions>;
    enabled?: MaybeRef<boolean>;
  } & Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn' | 'enabled'>
) {
  const baseKeyValue = computed<readonly unknown[]>(() =>
    deepUnref<readonly unknown[]>(baseKey as MaybeRef<readonly unknown[]>)
  );
  const params = computed<IServiceParams>(() =>
    deepUnref<IServiceParams>(args?.params ?? ({} as IServiceParams))
  );
  const options = computed<IServiceOptions>(() =>
    deepUnref<IServiceOptions>(args?.options ?? ({} as IServiceOptions))
  );
  const enabled = computed<boolean>(() =>
    args?.enabled === undefined ? true : unref(args.enabled)
  );

  const queryKey = computed(() => [...baseKeyValue.value, params.value] as const);

  return useQuery<TData, TError>({
    ...args,
    enabled,
    queryKey,
    queryFn: ({ signal }) => fetcher({ params: params.value, options: options.value, signal })
  });
}

export function useSmartMutation<TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: {
    onSuccess?: (
      data: TData,
      variables: TVariables,
      context: unknown,
      queryClient: ReturnType<typeof useQueryClient>
    ) => void;
    onError?: (
      error: unknown,
      variables: TVariables,
      context: unknown,
      queryClient: ReturnType<typeof useQueryClient>
    ) => void;
  } & Omit<Parameters<typeof useMutation>[1], 'onSuccess' | 'onError'>
) {
  const queryClient = useQueryClient();
  return useMutation<TData, unknown, TVariables>({
    mutationFn,
    ...options,
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context, queryClient);
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context, queryClient);
    }
  });
}

/** Default composable export */
export default function useQueryHelpers() {
  return {
    deepUnref,
    useSmartQuery,
    useSmartMutation
  };
}
